
import { mapboxgl } from './mapboxConfig';
import { Ride, Event } from '@/lib/types/core';

// Clear all markers except user location
export const clearMarkers = (markersRef: React.MutableRefObject<mapboxgl.Marker[]>) => {
  // Clear previous markers from the refs array
  markersRef.current.forEach(marker => marker.remove());
  markersRef.current = [];
};

// Add a default marker when no items exist
export const addDefaultMarker = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  mapLoaded: boolean,
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>
) => {
  if (!map.current || !mapLoaded) return;
  
  // Create a marker element with proper positioning
  const el = document.createElement('div');
  el.className = 'mapboxgl-marker-anchor';
  el.style.width = '30px';
  el.style.height = '30px';
  el.style.position = 'relative';
  
  const markerEl = document.createElement('div');
  markerEl.className = 'flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
  markerEl.innerHTML = `
    <div class="w-6 h-6 rounded-full bg-adv-orange opacity-30 absolute"></div>
    <div class="relative flex items-center justify-center w-6 h-6 rounded-full bg-adv-orange text-white">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 6.9 8 11.7z"></path>
        <circle cx="12" cy="12" r="10"></circle>
      </svg>
    </div>
  `;
  el.appendChild(markerEl);
  
  // Add marker at map center with proper anchor
  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom', // This ensures the bottom of the marker is at the exact coordinates
  })
    .setLngLat(map.current.getCenter())
    .addTo(map.current);
    
  markersRef.current.push(marker);
};

// Add ride markers to the map
export const addRideMarkers = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  mapLoaded: boolean,
  loading: boolean,
  rides: Ride[],
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
  userInteracted: boolean,
  setSelectedRide: (ride: Ride | null) => void
) => {
  if (!map.current || !mapLoaded || loading) return;
  
  // Clear existing markers first
  clearMarkers(markersRef);
  
  if (rides.length === 0) {
    // If no rides, add default marker at map center
    //addDefaultMarker(map, mapLoaded, markersRef);
    return;
  }
  
  // Create bounds to fit all markers
  const bounds = new mapboxgl.LngLatBounds();
  
  // Add markers for each ride
  rides.forEach(ride => {
    const [lng, lat] = [ride.meeting_longitude, ride.meeting_latitude];
    
    // Create custom marker element with proper positioning
    const el = document.createElement('div');
    el.className = 'mapboxgl-marker-anchor';
    el.style.width = '30px';
    el.style.height = '30px';
    el.style.position = 'relative';
    
    const markerEl = document.createElement('div');
    markerEl.className = 'flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
    markerEl.innerHTML = `
      <div class="w-6 h-6 rounded-full bg-adv-orange opacity-30 absolute"></div>
      <div class="relative flex items-center justify-center w-6 h-6 rounded-full bg-adv-orange text-white">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0z"></path>
          <path d="M10 0v8a2 2 0 0 0 4 0V0"></path>
        </svg>
      </div>
    `;
    el.appendChild(markerEl);
    
    // Create and add marker with proper anchor
    const marker = new mapboxgl.Marker({
      element: el,
      anchor: 'bottom', // This ensures the bottom of the marker is at the exact coordinates
    })
      .setLngLat([lng, lat])
      .addTo(map.current!);
    
    // Add to refs array for later cleanup
    markersRef.current.push(marker);
    
    // Add click event
    marker.getElement().addEventListener('click', () => {
      setSelectedRide(ride);
    });
    
    // Extend bounds
    bounds.extend([lng, lat]);
  });
  
  // Fit map to bounds with padding only if there are rides and the user hasn't interacted yet
  // or if we're showing rides for the first time
  if (!bounds.isEmpty() && !userInteracted && rides.length > 0) {
    map.current.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 },
      maxZoom: 12
    });
  }
};

// Add event markers to the map
export const addEventMarkers = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  mapLoaded: boolean,
  loading: boolean,
  events: Event[],
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
  userInteracted: boolean,
  eventType: 'all' | 'ride' | 'rally',
  setSelectedEvent: (event: Event | null) => void
) => {
  if (!map.current || !mapLoaded || loading) return;
  
  // Clear existing markers first
  clearMarkers(markersRef);
  
  if (events.length === 0) {
    // If no events, add default marker at map center
    addDefaultMarker(map, mapLoaded, markersRef);
    return;
  }
  
  // Create bounds to fit all markers
  const bounds = new mapboxgl.LngLatBounds();
  
  // Filter events by type if needed
  const filteredEvents = eventType === 'all' 
    ? events 
    : events.filter(event => event.event_type === eventType);
  
  // Add markers for each event
  filteredEvents.forEach(event => {
    const [lng, lat] = [event.meeting_longitude, event.meeting_latitude];
    
    // Create marker container with proper positioning
    const el = document.createElement('div');
    el.className = 'mapboxgl-marker-anchor';
    el.style.width = '30px';
    el.style.height = '30px';
    el.style.position = 'relative';
    
    const markerEl = document.createElement('div');
    markerEl.className = 'flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
    
    // Different styling for rides vs rallies
    if (event.event_type === 'rally') {
      markerEl.innerHTML = `
        <div class="w-6 h-6 rounded-full bg-green-500 opacity-30 absolute"></div>
        <div class="relative flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M7 20h10"></path>
            <path d="M10 20v-5a2 2 0 1 1 4 0v5"></path>
            <path d="M2 8l10-5 10 5"></path>
            <path d="M12 3v2"></path>
          </svg>
        </div>
      `;
    } else {
      markerEl.innerHTML = `
        <div class="w-6 h-6 rounded-full bg-adv-orange opacity-30 absolute"></div>
        <div class="relative flex items-center justify-center w-6 h-6 rounded-full bg-adv-orange text-white">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0z"></path>
            <path d="M10 0v8a2 2 0 0 0 4 0V0"></path>
          </svg>
        </div>
      `;
    }
    
    el.appendChild(markerEl);
    
    // Create and add marker with proper anchor
    const marker = new mapboxgl.Marker({
      element: el,
      anchor: 'bottom', // This ensures the bottom of the marker is at the exact coordinates
    })
      .setLngLat([lng, lat])
      .addTo(map.current!);
      
    // Add to refs array for later cleanup
    markersRef.current.push(marker);
    
    // Add click event
    marker.getElement().addEventListener('click', () => {
      setSelectedEvent(event);
    });
    
    // Extend bounds
    bounds.extend([lng, lat]);
  });
  
  // Fit map to bounds with padding only if there are events and the user hasn't interacted yet
  // or if we're showing events for the first time
  if (!bounds.isEmpty() && !userInteracted && filteredEvents.length > 0) {
    map.current.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 },
      maxZoom: 12
    });
  }
};
